// No Fuss Readers API (study-portal-server). Since the Oct 2026 security release its admin routes
// need `Authorization: Bearer <token>` from POST /api/auth/login, so the dashboard keeps a Readers
// admin session in localStorage and attaches it to every Readers request.
import { ApiError, toQuery, QueryValue } from './api';

export const READERS_BASE_URL = process.env.REACT_APP_STUDY_PORTAL_URL || '';
const TOKEN_KEY = 'nft_readers_token';
const EMAIL_KEY = 'nft_readers_email';
const EVENT = 'nft-readers-auth';

const storage = {
  get(key: string) {
    try {
      return window.localStorage.getItem(key);
    } catch (err) {
      return null;
    }
  },
  set(key: string, value: string | null) {
    try {
      if (value === null) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, value);
    } catch (err) {
      // storage blocked: the session just will not persist
    }
  },
};

export const getReadersToken = () => storage.get(TOKEN_KEY);
export const getReadersEmail = () => storage.get(EMAIL_KEY);

export const setReadersSession = (token: string | null, email: string | null = null) => {
  storage.set(TOKEN_KEY, token);
  storage.set(EMAIL_KEY, token ? email : null);
  window.dispatchEvent(new Event(EVENT));
};

export const onReadersAuthChange = (handler: () => void) => {
  window.addEventListener(EVENT, handler);
  return () => window.removeEventListener(EVENT, handler);
};

/** fetch() for the Readers API with the admin token attached. A 401 clears the stored session. */
export const readersFetch = async (input: string, init: RequestInit = {}) => {
  const token = getReadersToken();
  const headers: Record<string, string> = { ...((init.headers as Record<string, string>) || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(input, { ...init, headers });
  if (response.status === 401 && token) setReadersSession(null);
  return response;
};

export const readersUrl = (path: string, params: Record<string, QueryValue> = {}) =>
  `${READERS_BASE_URL}${path}${toQuery(params)}`;

export async function readersJSON<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await readersFetch(url, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...((init.headers as Record<string, string>) || {}) },
  });
  let body: any = null;
  try {
    body = await response.json();
  } catch (err) {
    body = null;
  }
  if (!response.ok) {
    throw new ApiError((body && body.message) || `Request failed (${response.status})`, response.status);
  }
  return body as T;
}

export type ReadersLoginResult = { token: string; user: any };

export const readersLogin = async (email: string, password: string) => {
  const result = await readersJSON<ReadersLoginResult>(`${READERS_BASE_URL}/api/auth/login`, {
    method: 'POST',
    body: JSON.stringify({ username: email.trim(), password }),
  });
  setReadersSession(result.token, email.trim().toLowerCase());
  return result;
};
